<template>
  <!-- Modal de Perfil -->
  <div class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- Overlay -->
      <div 
        class="fixed inset-0 transition-opacity bg-black bg-opacity-50"
        @click="$emit('close')"
      ></div>

      <!-- Modal -->
      <div class="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-discord-dark-300 shadow-xl rounded-lg">
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-lg font-medium text-discord-light-100">
            {{ t('profileModal.title') }}
          </h3>
          <button
            @click="$emit('close')"
            class="text-discord-light-600 hover:text-discord-light-100 transition-colors"
          >
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Loading state -->
        <div v-if="!authStore.user" class="text-center py-20">
          <div class="inline-flex items-center justify-center w-20 h-20 bg-discord-blurple rounded-full mb-6">
            <svg class="animate-spin h-10 w-10 text-white" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h3 class="text-2xl font-semibold text-discord-light-100 mb-3">{{ t('profileModal.loadingTitle') }}</h3>
          <p class="text-discord-light-600 text-lg">{{ t('profileModal.loadingSubtitle') }}</p>
        </div>

        <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <!-- Coluna Esquerda - Informações Pessoais -->
          <div class="space-y-6">
            <!-- Card de Informações Básicas -->
            <div class="bg-discord-dark-200 rounded-xl p-6 border border-discord-dark-100">
              <div class="flex items-center space-x-4 mb-6">
                <div class="w-16 h-16 rounded-full overflow-hidden border-4 border-discord-dark-100 shadow-lg bg-gradient-to-br from-discord-blurple to-discord-blurple-hover">
                  <img :src="resolvedPreviewFoto" class="w-full h-full object-cover" :alt="t('profileModal.avatarAlt')" />
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-discord-light-100">{{ authStore.user?.nomeCompleto }}</h3>
                  <p class="text-sm text-discord-light-400">{{ authStore.user?.email }}</p>
                  <div class="flex items-center mt-1">
                    <div class="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span class="text-xs text-green-400">{{ t('profileModal.online') }}</span>
                  </div>
                </div>
              </div>

              <!-- Estatísticas do Usuário -->
              <div class="grid grid-cols-2 gap-4">
                <div class="bg-discord-dark-100 rounded-lg p-3 text-center">
                  <p class="text-xs text-discord-light-500 uppercase tracking-wide">{{ t('profileModal.memberSince') }}</p>
                  <p class="text-sm font-medium text-discord-light-100 mt-1">{{ formatDate(memberSinceRaw) }}</p>
                </div>
                <div class="bg-discord-dark-100 rounded-lg p-3 text-center">
                  <p class="text-xs text-discord-light-500 uppercase tracking-wide">{{ t('profileModal.lastUpdate') }}</p>
                  <p class="text-sm font-medium text-discord-light-100 mt-1">{{ formatDate(lastUpdateRaw) }}</p>
                </div>
              </div>
              <div class="mt-4 flex items-center justify-center gap-2 border-t border-discord-dark-200 pt-4">
                <span class="text-[11px] font-semibold uppercase tracking-wider text-discord-light-500">{{ t('profileModal.plan') }}</span>
                <span
                  class="rounded-lg border border-discord-dark-100 bg-discord-dark-100 px-2.5 py-1 text-xs font-bold text-discord-light-100"
                >{{ meuPlano?.nome || t('profileModal.planFree') }}</span>
              </div>
            </div>

            <!-- Card de Foto do Perfil -->
            <div class="bg-discord-dark-200 rounded-xl p-6 border border-discord-dark-100">
              <h4 class="text-base font-semibold text-discord-light-100 mb-4 flex items-center">
                <svg class="w-5 h-5 mr-2 text-discord-blurple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
                {{ t('profileModal.photoTitle') }}
              </h4>
              
              <!-- Acordeão para escolher tipo de foto -->
              <div class="space-y-2">
                <!-- Opção Avatares -->
                <div class="border border-discord-dark-200 rounded-xl overflow-hidden transition-all duration-200 hover:border-discord-dark-100" :class="fotoType === 'avatar' ? 'border-discord-blurple shadow-lg shadow-discord-blurple/20' : ''">
                  <button
                    @click="setFotoType('avatar')"
                    type="button"
                    class="w-full p-5 text-left flex items-center justify-between transition-all duration-200"
                    :class="fotoType === 'avatar' ? 'bg-gradient-to-r from-discord-blurple/10 to-discord-blurple/5' : 'hover:bg-discord-dark-200/50'"
                    :disabled="isSaving"
                  >
                    <div class="flex items-center">
                      <div class="w-10 h-10 rounded-xl flex items-center justify-center mr-4 transition-all duration-200" :class="fotoType === 'avatar' ? 'bg-discord-blurple shadow-lg' : 'bg-discord-dark-200'">
                        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                        </svg>
                      </div>
                      <div>
                        <p class="font-semibold text-discord-light-100 text-base">{{ t('profileModal.avatars') }}</p>
                        <p class="text-sm text-discord-light-500">{{ t('profileModal.avatarsHint') }}</p>
                      </div>
                    </div>
                    <div class="flex items-center">
                      <div v-if="fotoType === 'avatar'" class="w-2 h-2 bg-discord-blurple rounded-full mr-3"></div>
                      <svg 
                        class="w-5 h-5 text-discord-light-400 transition-all duration-200"
                        :class="fotoType === 'avatar' ? 'rotate-180 text-discord-blurple' : ''"
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                      </svg>
                    </div>
                  </button>
                  
                  <!-- Conteúdo dos avatares -->
                  <div v-if="fotoType === 'avatar'" class="px-5 pb-5 bg-gradient-to-b from-discord-blurple/5 to-transparent">
                    <div class="border-t border-discord-dark-200 pt-4">
                      <div class="grid grid-cols-5 sm:grid-cols-8 gap-2 max-h-40 overflow-y-auto p-3 bg-discord-dark-100 rounded-lg">
                        <button
                          v-for="avatar in avatars"
                          :key="avatar"
                          type="button"
                          @click="selectAvatar(avatar)"
                          :class="[
                            'w-10 h-10 rounded-full overflow-hidden border-2 transition-all transform hover:scale-110',
                            selectedAvatar === avatar ? 'border-discord-blurple ring-2 ring-discord-blurple ring-offset-2 ring-offset-discord-dark-200' : 'border-discord-dark-50 hover:border-discord-light-400'
                          ]"
                          :disabled="isSaving"
                        >
                          <img :src="avatar" :alt="t('profileModal.avatarAlt')" class="w-full h-full object-cover" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Opção Upload -->
                <div class="border border-discord-dark-200 rounded-xl overflow-hidden transition-all duration-200 hover:border-discord-dark-100" :class="fotoType === 'upload' ? 'border-discord-green shadow-lg shadow-discord-green/20' : ''">
                  <button
                    @click="setFotoType('upload')"
                    type="button"
                    class="w-full p-5 text-left flex items-center justify-between transition-all duration-200"
                    :class="fotoType === 'upload' ? 'bg-gradient-to-r from-discord-green/10 to-discord-green/5' : 'hover:bg-discord-dark-200/50'"
                    :disabled="isSaving"
                  >
                    <div class="flex items-center">
                      <div class="w-10 h-10 rounded-xl flex items-center justify-center mr-4 transition-all duration-200" :class="fotoType === 'upload' ? 'bg-discord-green shadow-lg' : 'bg-discord-dark-200'">
                        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                        </svg>
                      </div>
                      <div>
                        <p class="font-semibold text-discord-light-100 text-base">{{ t('profileModal.upload') }}</p>
                        <p class="text-sm text-discord-light-500">{{ t('profileModal.uploadHint') }}</p>
                      </div>
                    </div>
                    <div class="flex items-center">
                      <div v-if="fotoType === 'upload'" class="w-2 h-2 bg-discord-green rounded-full mr-3"></div>
                      <svg 
                        class="w-5 h-5 text-discord-light-400 transition-all duration-200"
                        :class="fotoType === 'upload' ? 'rotate-180 text-discord-green' : ''"
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                      </svg>
                    </div>
                  </button>
                  
                  <!-- Conteúdo do upload -->
                  <div v-if="fotoType === 'upload'" class="px-5 pb-5 bg-gradient-to-b from-discord-green/5 to-transparent">
                    <div class="border-t border-discord-dark-200 pt-4">
                      <!-- Preview da imagem atual (se houver) -->
                      <div v-if="form.fotoPerfil && form.fotoPerfil !== ''" class="mb-4">
                        <div class="flex items-center justify-between p-3 bg-discord-dark-100 rounded-lg">
                          <div class="flex items-center">
                            <img :src="getFileUrl(form.fotoPerfil) || form.fotoPerfil" :alt="t('profileModal.currentImage')" class="w-12 h-12 object-cover rounded-lg mr-3" />
                            <div>
                              <p class="text-sm font-medium text-discord-light-100">{{ t('profileModal.currentImage') }}</p>
                              <p class="text-xs text-discord-light-500">{{ t('profileModal.removeImageHint') }}</p>
                            </div>
                          </div>
                          <button
                            @click="removeUploadedImage"
                            type="button"
                            class="p-2 text-discord-light-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all duration-200"
                            :title="t('profileModal.removeImageTitle')"
                            :disabled="isSaving"
                          >
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      <!-- Botão de upload -->
                      <div class="relative">
                        <input
                          type="file"
                          ref="fileInput"
                          @change="handleFileSelect"
                          accept="image/*"
                          class="hidden"
                          :disabled="isSaving"
                        />
                        <button
                          type="button"
                          @click="triggerFileSelect"
                          class="w-full p-6 border-2 border-dashed border-discord-dark-100 rounded-lg text-discord-light-400 hover:border-discord-green hover:text-discord-light-300 hover:bg-discord-dark-100 transition-all duration-200 group"
                          :disabled="isSaving"
                        >
                          <div class="flex flex-col items-center">
                            <div class="w-12 h-12 rounded-full bg-discord-dark-100 flex items-center justify-center mb-3 group-hover:bg-discord-green transition-colors">
                              <svg class="w-6 h-6 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                              </svg>
                            </div>
                            <span class="text-sm font-medium">{{ t('profileModal.uploadPhoto') }}</span>
                            <span class="text-xs text-discord-light-500 mt-1">{{ t('profileModal.uploadFormats') }}</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Coluna Direita - Formulários (sem form aninhado: senha em modal) -->
          <div class="space-y-6">
            <div class="bg-discord-dark-200 rounded-xl p-6 border border-discord-dark-100">
              <h4 class="text-base font-semibold text-discord-light-100 mb-4 flex items-center">
                <svg class="w-5 h-5 mr-2 text-discord-blurple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
                {{ t('profileModal.personalInfo') }}
              </h4>

              <div class="space-y-4">
                <div>
                  <label for="nomeCompleto" class="block text-sm font-medium text-discord-light-100 mb-2">
                    {{ t('profileModal.fullName') }}
                  </label>
                  <input
                    id="nomeCompleto"
                    v-model="form.nomeCompleto"
                    type="text"
                    class="input-field"
                    :placeholder="t('profileModal.fullNamePlaceholder')"
                    :disabled="isSaving"
                  />
                </div>

                <div>
                  <label for="email" class="block text-sm font-medium text-discord-light-100 mb-2">
                    {{ t('common.email') }}
                  </label>
                  <input
                    id="email"
                    v-model="form.email"
                    type="email"
                    class="input-field"
                    :placeholder="t('profileModal.emailPlaceholder')"
                    :disabled="isSaving"
                  />
                </div>
              </div>
            </div>

            <div class="bg-discord-dark-200 rounded-xl p-6 border border-discord-dark-100">
              <h4 class="text-base font-semibold text-discord-light-100 mb-2 flex items-center">
                <svg class="w-5 h-5 mr-2 text-discord-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
                {{ t('profileModal.security') }}
              </h4>
              <p class="text-sm text-discord-light-500 mb-4">{{ t('profileModal.securityNote') }}</p>
              <button
                type="button"
                class="w-full px-4 py-2 text-sm font-medium text-white bg-discord-red border border-transparent rounded-lg hover:bg-discord-red-hover focus:outline-none focus:ring-2 focus:ring-discord-red focus:ring-offset-2 focus:ring-offset-discord-dark-200 transition-all"
                :disabled="isSaving || isSavingPwd"
                @click="openPwdModal"
              >
                {{ t('profileModal.changePassword') }}
              </button>
            </div>

            <div class="flex space-x-3 pt-2">
              <button
                type="button"
                class="flex-1 px-4 py-2 text-sm font-medium text-discord-light-600 bg-discord-dark-100 border border-discord-dark-50 rounded-lg hover:bg-discord-dark-50 hover:text-discord-light-100 focus:outline-none focus:ring-2 focus:ring-discord-blurple focus:ring-offset-2 focus:ring-offset-discord-dark-200 transition-all"
                @click="$emit('close')"
              >
                {{ t('common.cancel') }}
              </button>
              <button
                type="button"
                :disabled="isSaving"
                class="flex-1 px-4 py-2 text-sm font-medium text-white bg-discord-blurple border border-transparent rounded-lg hover:bg-discord-blurple-hover focus:outline-none focus:ring-2 focus:ring-discord-blurple focus:ring-offset-2 focus:ring-offset-discord-dark-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center shadow-lg"
                @click="salvarPerfil"
              >
                <svg v-if="isSaving" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ isSaving ? t('common.saving') : t('profileModal.saveChanges') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload + Crop Modal - Fora do modal principal -->
    <teleport to="body">
      <div v-if="showUploader" class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60]">
        <div class="bg-discord-dark-300 rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <ImageUploadCrop 
            :selected-file="selectedFile"
            @image-uploaded="onImageUploaded" 
            @close="closeUploader" 
          />
        </div>
      </div>
    </teleport>

    <teleport to="body">
      <div
        v-if="showPwdModal"
        class="fixed inset-0 z-[70] flex items-center justify-center px-4 bg-black/60 backdrop-blur-[2px]"
        @click.self="closePwdModal"
      >
        <div
          class="relative w-full max-w-md overflow-hidden rounded-2xl border shadow-2xl"
          style="
            background: var(--bg-card);
            border-color: var(--border-secondary);
            box-shadow: 0 25px 50px -12px color-mix(in srgb, var(--bg-primary) 55%, transparent);
          "
          @click.stop
        >
          <div
            class="h-1 w-full"
            style="background: linear-gradient(90deg, var(--accent-primary), var(--info), var(--success))"
          />
          <div class="p-6 pt-5">
            <div class="mb-5 flex items-start gap-3">
              <div
                class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                style="
                  background: color-mix(in srgb, var(--accent-primary) 22%, transparent);
                  color: var(--accent-primary);
                "
              >
                <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <div class="min-w-0 flex-1">
                <h4 class="text-lg font-semibold leading-tight" style="color: var(--text-primary)">{{ t('profileModal.pwdModalTitle') }}</h4>
                <p class="mt-1 text-sm leading-relaxed" style="color: var(--text-secondary)">
                  {{ t('profileModal.pwdModalSub') }}
                </p>
              </div>
            </div>

            <form class="space-y-1" @submit.prevent="alterarSenha">
              <PasswordFieldsBlock
                id-prefix="profile-pwd"
                :senha-atual="pwd.senhaAtual"
                :nova-senha="pwd.novaSenha"
                :confirmar-nova-senha="pwd.confirmarNovaSenha"
                :disabled="isSavingPwd"
                @update:senha-atual="(v) => (pwd.senhaAtual = v)"
                @update:nova-senha="(v) => (pwd.novaSenha = v)"
                @update:confirmar-nova-senha="(v) => (pwd.confirmarNovaSenha = v)"
              />

              <p v-if="!canSubmitPwd && !isSavingPwd" class="pt-1 text-center text-xs" style="color: var(--text-tertiary)">
                {{ t('profileModal.pwdModalHint') }}
              </p>

              <div class="flex gap-3 pt-4">
                <button
                  type="button"
                  class="flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors"
                  style="color: var(--text-secondary); background: var(--bg-hover)"
                  :disabled="isSavingPwd"
                  @click="closePwdModal"
                >
                  {{ t('common.cancel') }}
                </button>
                <button
                  type="submit"
                  class="flex-1 flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
                  style="background: var(--error)"
                  :disabled="isSavingPwd || !canSubmitPwd"
                >
                  <svg
                    v-if="isSavingPwd"
                    class="mr-2 h-4 w-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  {{ isSavingPwd ? t('common.saving') : t('common.confirm') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { formatDate as formatDateIntl } from '../utils/format'
import userApi from '../services/user'
import api from '../services/api'
import ImageUploadCrop from './ImageUploadCrop.vue'
import PasswordFieldsBlock from './PasswordFieldsBlock.vue'
import { useToast } from 'vue-toastification'
import { getFileUrl, joinUrl, config } from '../config/app'
import Swal from 'sweetalert2'

// Emits
const emit = defineEmits(['close'])

// Stores e composables
const authStore = useAuthStore()
const toast = useToast()
const { t, locale } = useI18n()

// Estado
const isSaving = ref(false)
const isSavingPwd = ref(false)
const showUploader = ref(false)
const showPwdModal = ref(false)
const meuPlano = ref(null)
const nowFallback = ref(new Date().toISOString())

const form = ref({ nomeCompleto: '', email: '', fotoPerfil: null })
const pwd = ref({ senhaAtual: '', novaSenha: '', confirmarNovaSenha: '' })

const canSubmitPwd = computed(() => {
  const atual = pwd.value.senhaAtual?.trim()
  const nova = (pwd.value.novaSenha || '').trim()
  const conf = pwd.value.confirmarNovaSenha || ''
  return (
    !!atual &&
    nova.length >= 6 &&
    conf.length > 0 &&
    pwd.value.novaSenha === pwd.value.confirmarNovaSenha
  )
})

const selectedAvatar = ref(null)
const uploadedFoto = ref(null)

// Acordeão de foto
const fotoType = ref('avatar') // 'avatar' ou 'upload'

// Upload de arquivo
const fileInput = ref(null)
const selectedFile = ref(null)

// Avatars — base em config.fileServerUrl (dev/prod)
const avatars = Array.from({ length: 35 }, (_, i) =>
  joinUrl(config.fileServerUrl, `avatars/${i + 1}.png`)
)

const previewFoto = computed(() => uploadedFoto.value || selectedAvatar.value || form.value.fotoPerfil || '/logo_w.png')
const resolvedPreviewFoto = computed(() => getFileUrl(previewFoto.value) || previewFoto.value)

/** ISO, timestamp, array Jackson [y,m,d,h,min,s] ou objeto com year/monthValue */
function parseUserDate(raw) {
  if (raw == null || raw === '') return null
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const d = new Date(raw)
    return Number.isNaN(d.getTime()) ? null : d
  }
  if (Array.isArray(raw)) {
    const y = raw[0]
    const m = raw[1] ?? 1
    const day = raw[2] ?? 1
    const h = raw[3] ?? 0
    const min = raw[4] ?? 0
    const sec = raw[5] ?? 0
    if (y == null) return null
    const d = new Date(y, m - 1, day, h, min, sec)
    return Number.isNaN(d.getTime()) ? null : d
  }
  if (typeof raw === 'object') {
    if (raw.year != null) {
      const d = new Date(
        raw.year,
        (raw.monthValue ?? raw.month ?? 1) - 1,
        raw.dayOfMonth ?? raw.day ?? 1,
        raw.hour ?? 0,
        raw.minute ?? 0,
        raw.second ?? 0
      )
      return Number.isNaN(d.getTime()) ? null : d
    }
  }
  const str = String(raw).trim()
  if (!str) return null
  const d = new Date(str)
  return Number.isNaN(d.getTime()) ? null : d
}

const memberSinceRaw = computed(() => authStore.user?.dataCriacao ?? null)
const lastUpdateRaw = computed(() => {
  const u = authStore.user
  if (!u) return null
  return u.dataUpdate ?? u.dataAtualizacao ?? u.dataCriacao ?? null
})

const formatDate = (dateInput) => {
  const fallback = formatDateIntl(nowFallback.value, locale.value)
  if (dateInput == null || dateInput === '') {
    return fallback
  }
  const d = parseUserDate(dateInput)
  if (!d) {
    return fallback
  }
  return formatDateIntl(d, locale.value)
}

const persistFotoPerfil = async (fotoUrl) => {
  const url = fotoUrl == null ? '' : String(fotoUrl).trim()
  isSaving.value = true
  try {
    const dadosAtualizacao = {
      nomeCompleto: form.value.nomeCompleto,
      email: form.value.email,
      fotoPerfil: url
    }
    const { data } = await userApi.updateMe(dadosAtualizacao)
    authStore.updateUser(data)
    form.value.fotoPerfil = data?.fotoPerfil ?? url
    if (url) {
      uploadedFoto.value = form.value.fotoPerfil
    } else {
      uploadedFoto.value = null
      selectedAvatar.value = null
    }
    toast.success(t('profileModal.toast.photoUpdated'))
  } catch (error) {
    console.error('Erro ao salvar foto:', error)
    toast.error(t('profileModal.toast.photoError'))
  } finally {
    isSaving.value = false
  }
}

const openPwdModal = () => {
  pwd.value = { senhaAtual: '', novaSenha: '', confirmarNovaSenha: '' }
  showPwdModal.value = true
}

const closePwdModal = () => {
  showPwdModal.value = false
  pwd.value = { senhaAtual: '', novaSenha: '', confirmarNovaSenha: '' }
}

// Funções
const carregarMe = async () => {
  try {
    const [{ data }, { data: plano }] = await Promise.all([
      userApi.getMe(),
      api.get('/planos/meu-plano')
    ])
    if (data) {
      form.value.nomeCompleto = data.nomeCompleto || ''
      form.value.email = data.email || ''
      form.value.fotoPerfil = data.fotoPerfil || null
      meuPlano.value = plano || null
      authStore.updateUser({ ...(data || {}), planoCodigo: plano?.codigo || data?.planoCodigo })
    }
  } catch (error) {
    console.error('❌ ProfileModal: Erro ao carregar perfil:', error)
    toast.error(t('profileModal.toast.loadError'))
  }
}

const salvarPerfil = async () => {
  isSaving.value = true
  try {
    const dadosAtualizacao = {
      nomeCompleto: form.value.nomeCompleto,
      email: form.value.email,
      fotoPerfil: selectedAvatar.value || uploadedFoto.value || form.value.fotoPerfil || ''
    }

    const { data } = await userApi.updateMe(dadosAtualizacao)

    authStore.updateUser(data)
    if (data?.fotoPerfil != null) {
      form.value.fotoPerfil = data.fotoPerfil
    }
    
    // SweetAlert de sucesso
    await Swal.fire({
      title: t('profileModal.swal.successTitle'),
      text: t('profileModal.swal.successText'),
      icon: 'success',
      confirmButtonText: t('common.confirm'),
      confirmButtonColor: '#5865F2',
      background: '#2c2f33',
      color: '#ffffff',
      customClass: {
        confirmButton: 'bg-discord-blurple hover:bg-discord-blurple-hover'
      }
    })
    
    // Fechar modal após sucesso
    emit('close')
  } catch (error) {
    console.error('Erro ao atualizar perfil:', error)
    
    // SweetAlert de erro
    await Swal.fire({
      title: t('profileModal.swal.errorTitle'),
      text: t('profileModal.swal.errorText'),
      icon: 'error',
      confirmButtonText: t('common.confirm'),
      confirmButtonColor: '#ed4245',
      background: '#2c2f33',
      color: '#ffffff'
    })
  } finally {
    isSaving.value = false
  }
}

const alterarSenha = async () => {
  if (!canSubmitPwd.value) {
    toast.error(t('profileModal.toast.pwdValidation'))
    return
  }
  if (pwd.value.novaSenha !== pwd.value.confirmarNovaSenha) {
    toast.error(t('profileModal.toast.pwdMismatch'))
    return
  }
  isSavingPwd.value = true
  try {
    await userApi.updatePassword({
      senhaAtual: pwd.value.senhaAtual,
      novaSenha: pwd.value.novaSenha
    })

    pwd.value.senhaAtual = ''
    pwd.value.novaSenha = ''
    pwd.value.confirmarNovaSenha = ''

    toast.success(t('profileModal.toast.passwordChanged'))
    closePwdModal()
  } catch (error) {
    console.error('Erro ao alterar senha:', error)
    toast.error(t('profileModal.toast.passwordError'))
  } finally {
    isSavingPwd.value = false
  }
}

const selectAvatar = async (avatar) => {
  selectedAvatar.value = avatar
  uploadedFoto.value = null
  if (fotoType.value !== 'avatar') {
    fotoType.value = 'avatar'
  }
  await persistFotoPerfil(avatar)
}

const onImageUploaded = async (imageUrl) => {
  uploadedFoto.value = imageUrl
  selectedAvatar.value = null
  showUploader.value = false
  selectedFile.value = null
  if (fotoType.value !== 'upload') {
    fotoType.value = 'upload'
  }
  form.value.fotoPerfil = imageUrl
  await persistFotoPerfil(imageUrl)
}

// Funções do acordeão
const setFotoType = (type) => {
  fotoType.value = type
}

const removeUploadedImage = async () => {
  uploadedFoto.value = null
  form.value.fotoPerfil = null
  await persistFotoPerfil('')
}

const closeUploader = () => {
  showUploader.value = false
  selectedFile.value = null
}

// Funções de upload de arquivo
const triggerFileSelect = () => {
  if (fileInput.value) {
    fileInput.value.click()
  }
}

const handleFileSelect = (event) => {
  const file = event.target.files[0]
  if (file) {
    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      Swal.fire({
        title: t('profileModal.swal.errorTitle'),
        text: t('profileModal.swal.imageOnly'),
        icon: 'error',
        confirmButtonText: t('common.confirm'),
        confirmButtonColor: '#ed4245',
        background: '#2c2f33',
        color: '#ffffff'
      })
      return
    }
    
    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        title: t('profileModal.swal.errorTitle'),
        text: t('profileModal.swal.fileSize'),
        icon: 'error',
        confirmButtonText: t('common.confirm'),
        confirmButtonColor: '#ed4245',
        background: '#2c2f33',
        color: '#ffffff'
      })
      return
    }
    
    // Armazenar arquivo selecionado
    selectedFile.value = file
    
    // Abrir modal de crop
    showUploader.value = true
    
    // Limpar input
    event.target.value = ''
  }
}

// Lifecycle
onMounted(async () => {
  if (!authStore.isAuthenticated) {
    return
  }
  
  try {
    // Carregar dados do usuário se necessário
    if (!authStore.user) {
      await authStore.fetchUser()
    }
    
    // Carregar dados do perfil
    await carregarMe()
    
    // Determinar tipo inicial baseado na foto atual
    if (form.value.fotoPerfil && form.value.fotoPerfil !== '') {
      // Se tem foto, verificar se é um avatar ou upload
      const isAvatar = avatars.some(avatar => avatar === form.value.fotoPerfil)
      fotoType.value = isAvatar ? 'avatar' : 'upload'
      if (isAvatar) {
        selectedAvatar.value = form.value.fotoPerfil
      } else {
        uploadedFoto.value = form.value.fotoPerfil
      }
    } else {
      // Se não tem foto, começar com avatar
      fotoType.value = 'avatar'
    }
  } catch (error) {
    console.error('Erro ao carregar perfil:', error)
  }
})
</script>

<style scoped>
/* Input field seguindo padrão do EditWorkspaceModal */
.input-field {
  @apply w-full px-3 py-2 bg-discord-dark-200 border border-discord-dark-100 rounded-lg text-discord-light-100 placeholder-discord-light-500 focus:outline-none focus:ring-2 focus:ring-discord-blurple focus:border-transparent transition-all;
}

.input-field:disabled {
  @apply opacity-50 cursor-not-allowed;
}

/* Cards com efeito de elevação sutil */
.bg-discord-dark-200 {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease-in-out;
}

.bg-discord-dark-200:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Avatar com gradiente sutil */
.bg-gradient-to-br {
  background: linear-gradient(135deg, #5865F2 0%, #4752C4 100%);
}

/* Animações suaves para botões de avatar */
.transform {
  transition: all 0.2s ease-in-out;
}

.hover\:scale-110:hover {
  transform: scale(1.1);
}

/* Ring offset personalizado */
.ring-offset-discord-dark-200 {
  --tw-ring-offset-color: #2c2f33;
}

/* Scrollbar personalizada com design mais elegante */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(44, 47, 51, 0.5);
  border-radius: 4px;
  margin: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #5865F2 0%, #4752C4 100%);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #4752C4 0%, #3C45A5 100%);
}

/* Efeito de brilho sutil nos cards */
.bg-discord-dark-200::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(88, 101, 242, 0.3), transparent);
  border-radius: 8px 8px 0 0;
}

/* Animações de entrada */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.space-y-6 > * {
  animation: fadeInUp 0.3s ease-out;
}

.space-y-6 > *:nth-child(2) {
  animation-delay: 0.1s;
}

.space-y-6 > *:nth-child(3) {
  animation-delay: 0.2s;
}

/* Status online com pulso */
.bg-green-500 {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
